import { Link } from 'react-router';
import { LINKS } from '@/shared/config/links';
import { ROUTES } from '@/shared/config/routes';
import type { TestPodRunData } from '@/shared/types/workspace';
import { Banner } from '@/shared/ui/banner';
import { Button } from '@/shared/ui/button';
import { useStartTestPod } from '../hooks/use-start-test-pod';
import { testPodLines } from '../model/test-pod';
import { TestPodActions } from './test-pod-actions';
import { TestPodLog } from './test-pod-log';

type TestPodStepProps = {
  run: TestPodRunData | null;
  agentName: string;
  environmentName: string | null;
};

export function TestPodStep({ run, agentName, environmentName }: TestPodStepProps) {
  const { startTestPod, isStarting } = useStartTestPod();
  const status = run?.status ?? null;
  const isUnavailable = status === 'UNAVAILABLE';
  const isPassed = status === 'PASSED';
  const failedRun = status === 'FAILED' ? run : null;

  return (
    <>
      <p className="text-muted">
        We boot a clean Pod, build a sample app and take a Simulator screenshot. It uses about 0.03
        Pod-hours.
      </p>
      <TestPodLog
        lines={testPodLines(run)}
        emptyText={isUnavailable ? 'Pods are not open yet.' : 'Ready when you are.'}
      />
      {isUnavailable ? (
        <Banner
          tone="info"
          size="compact"
          action={
            <Button asChild variant="primary" size="sm">
              <Link to={ROUTES.OVERVIEW}>Go to Overview</Link>
            </Button>
          }
        >
          Your agent is connected. Pods are not open yet, so the test Pod cannot run today. Your 3
          free Pod-hours wait until it can.
        </Banner>
      ) : null}
      {failedRun ? (
        <div role="alert">
          <Banner tone="bad" size="compact" title="The test Pod failed on our side, not yours.">
            Try again. If it fails twice, write to {LINKS.SUPPORT_EMAIL} and mention{' '}
            {failedRun.reference}.
          </Banner>
        </div>
      ) : null}
      {isUnavailable ? null : (
        <TestPodActions
          isPassed={isPassed}
          isFailed={failedRun !== null}
          isRunning={isStarting || status === 'QUEUED' || status === 'RUNNING'}
          onRun={() => void startTestPod()}
        />
      )}
      {isPassed ? (
        <p className="text-muted">
          Next: start a session in {agentName} and pick the{' '}
          {environmentName ? (
            <span className="font-mono text-ink text-mono">{environmentName}</span>
          ) : (
            'Macswarm'
          )}{' '}
          environment. Your agents get a Pod in seconds.
        </p>
      ) : null}
    </>
  );
}
